
import WDPaneList from './wd-pane-list';

export default class WDPaneListPager extends WDPaneList
{
  #pager = null;
  #pagerCounter;
  #totalCount;
  #resizeObserver = null;
  #resizeTimeout = null;
  #windowResizeListener = null;
  
  // Responsive pager configuration
  #config = {
    pager: {
      minPages: 3,        // Minimum pages to show
      maxPages: 15,       // Maximum pages to show
      compactThreshold: 576,  // Bootstrap sm breakpoint
      touchTarget: 44,    // Minimum touch target size (iOS guidelines)
      segments: {
        xs: { width: 40, max: 5 },    // < 576px
        sm: { width: 45, max: 7 },    // 576px - 768px  
        md: { width: 50, max: 9 },    // 768px - 992px
        lg: { width: 55, max: 12 },   // 992px - 1200px
        xl: { width: 60, max: 15 }    // > 1200px
      }
    }
  };

  constructor(cfg)
  {
    super(cfg);
    this.classList.add('has-pager');
    
    // Merge user config with defaults
    if (cfg?.pager) {
      this.#config.pager = { ...this.#config.pager, ...cfg.pager };
    }

    this.on('model:fetched',(evt) => {
      this.#totalCount = evt.detail.total_count;
      this.showPager();
    });
  }

  /**
   * @method pagerbar
   */
  get pager()
  {
    if (!this.#pager)
    {
      const m = this.toolbar.prefix;
      m.classList.add('wd-pager-counter');
      this.#pagerCounter = m.addText('');

      this.#pager = this.toolbar.addPager();
      this.#pager.on('page:selected', (evt) => this.offset = evt.detail.page);

      // Ensure we have an offset
      if (this.fetch.limit && !this.fetch.offset) {
        this.fetch.offset = '0';
      }

      // Use ResizeObserver for better performance than window resize
      this.#setupResizeObserver();
      
      // Listen for pane changes
      this.panes.on('pane:added', () => this.showPager());
      this.panes.on('pane:removed', () => this.showPager());
    }

    return this.#pager;
  }

  #setupResizeObserver()
  {
    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        // Debounce resize events
        clearTimeout(this.#resizeTimeout);
        this.#resizeTimeout = setTimeout(() => this.showPager(), 150);
      });
      this.#resizeObserver.observe(this);
    } else {
      // Fallback for older browsers
      this.#windowResizeListener = () => {
        clearTimeout(this.#resizeTimeout);
        this.#resizeTimeout = setTimeout(() => this.showPager(), 150);
      };
      window.addEventListener("resize", this.#windowResizeListener);
    }
  }

  #getResponsiveConfig()
  {
    const width = this.clientWidth;
  
    const { segments, minPages, maxPages, compactThreshold } = this.#config.pager;
    
    let config;
    if (width < 576) {
      config = segments.xs;
    } else if (width < 768) {
      config = segments.sm;
    } else if (width < 992) {
      config = segments.md;
    } else if (width < 1200) {
      config = segments.lg;
    } else {
      config = segments.xl;
    }

    // Calculate optimal page count based on available width
    const availableWidth = width - 200; // Account for counter and controls
    const calculatedMax = Math.floor(availableWidth / config.width);
    
    return {
      ...config,
      max: Math.max(minPages, Math.min(maxPages, calculatedMax, config.max)),
      isCompact: width < compactThreshold
    };
  }

  set offset(idx)
  {
    this.fetch.offset = idx * this.fetch.limit;
    this.fetchModel();
  }

  resetOffset()
  {
    this.fetch.offset = 0;
    if (this.#pager) this.#pager.page = '0';
  }

  showPager()
  {
    //something not initted yet - bail
    if (!this.#config?.pager) return;

    const { max, isCompact } = this.#getResponsiveConfig();
    const totalPages = Math.ceil(this.#totalCount / this.fetch.limit);
    
    if (!this.list) return;

    // Configure pager with responsive settings
    this.pager.load({
      count: this.#totalCount,
      limit: this.fetch.limit,
      max_pages: Math.min(max, totalPages),
      compact: isCompact,
      showPrevNext: true,
      maxVisible: max
    });

    // Update counter text
    const start = parseInt(this.fetch.offset) + 1;
    const end = Math.min(
      this.#totalCount, 
      parseInt(this.fetch.limit) + parseInt(this.fetch.offset)
    );
    
    const counterText = isCompact 
      ? `${start}-${end}/${this.#totalCount}` 
      : `${start}-${end} of ${this.#totalCount}`;
      
    this.#pagerCounter.innerText = counterText;
    this.updateFooter(counterText);
  }

  selectPage(idx)
  {
    this.offset = idx;
  }

  updateFooter(txt)
  {
    // Enhanced footer with better responsive design
    // list.footer.content is a wdc() node, must use while loop to trigger disconnectedCallback
    const footerContent = this.list.footer.content;
    while (footerContent.firstChild) {
      footerContent.firstChild.remove();
    }

    footerContent.append(txt);   
  }

  cleanupCallback()
  {
    // Call parent cleanup FIRST
    super.cleanupCallback();
    
    // Clean up ResizeObserver
    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
      this.#resizeObserver = null;
    }
    
    // Clear resize timeout
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = null;
    }
    
    // Clean up window resize listener
    if (this.#windowResizeListener) {
      window.removeEventListener("resize", this.#windowResizeListener);
      this.#windowResizeListener = null;
    }
    
    this.#pager = null;
    this.#pagerCounter = null;
    
    // Clean up config
    this.#config = null;
    this.#totalCount = null;
  }

  // New utility methods
  getCurrentPage()
  {
    return Math.floor(this.fetch.offset / this.fetch.limit);
  }

  getTotalPages()
  {
    return Math.ceil(this.#totalCount / this.fetch.limit);
  }

  hasNextPage()
  {
    return this.getCurrentPage() < this.getTotalPages() - 1;
  }

  hasPreviousPage()
  {
    return this.getCurrentPage() > 0;
  }
}

customElements.define('wd-pane-list-pager', WDPaneListPager);
